import { useRef, useState } from 'react';
import { Camera, Check, LoaderCircle, Wallet } from 'lucide-react';

import { deliveriesApi } from '@/api/deliveries.api';
import type { DeliveryDetail } from '@/api/types';
import { errorMessage } from '@/lib/errors';
import { formatMoney, isZeroMoney } from '@/lib/format';
import { isCashOnDelivery } from '@/lib/status';
import { Button } from './ui/button';
import { Card, CardBody } from './ui/card';
import { Sheet, SheetContent } from './ui/sheet';
import { toast } from '@/lib/toast';

/**
 * The handover at the door: the customer's code, and optionally a photograph.
 *
 * The code is the whole point of the sheet. It is minted at pickup, hashed
 * server-side, and never readable again — so it is the one piece of evidence
 * that the bag reached the person who ordered it rather than a doorstep. The
 * rider types what the customer reads out; the backend compares hashes and
 * counts wrong attempts, which is what makes four digits safe.
 */
export function HandoverSheet({
  delivery,
  open,
  onOpenChange,
  onDelivered,
}: {
  delivery: DeliveryDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelivered: (updated: DeliveryDetail) => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  /*
   * The storage key from a photograph already uploaded.
   *
   * Held across attempts on purpose: a wrong code must not cost the rider the
   * upload they already paid for on a stairwell connection.
   */
  const [proofKey, setProofKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const cod = isCashOnDelivery(delivery) && !isZeroMoney(delivery.codAmount);
  const ready = code.length === 4;

  const reset = () => {
    setCode('');
    setError(null);
    setProofKey(null);
    setUploading(false);
  };

  async function attachPhoto(file: File) {
    setUploading(true);
    setError(null);
    try {
      setProofKey(await deliveriesApi.uploadProof(delivery.id, file));
      toast.success('Photo attached');
    } catch (uploadError) {
      setError(errorMessage(uploadError));
    } finally {
      setUploading(false);
      // Cleared so choosing the same file again still fires a change event —
      // a retake of an identical filename is otherwise silently ignored.
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function complete() {
    if (working || !ready) return;
    setWorking(true);
    setError(null);
    try {
      const updated = await deliveriesApi.deliver(delivery.id, {
        otp: code,
        proofOfDeliveryKey: proofKey,
      });
      onDelivered(updated);
      onOpenChange(false);
      reset();
    } catch (deliverError) {
      // The server's own sentence — "Incorrect code. 3 attempt(s) left." —
      // because the attempts remaining is the only number that matters here
      // and no generic apology carries it.
      setError(errorMessage(deliverError));
      // Cleared so the rider re-reads the code off the customer rather than
      // retyping the same wrong four digits into the attempt counter.
      setCode('');
    } finally {
      setWorking(false);
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <SheetContent
        title="Hand over the order"
        description={`Ask ${delivery.customerName ?? 'the customer'} for their four-digit code.`}
      >
        {/*
         * Cash FIRST, before the code field.
         *
         * The order matters: once the code is accepted the delivery is
         * DELIVERED and the cash is recorded as being in this rider's custody
         * whether or not it is in their hand. Putting the amount after the
         * field would put it after the moment it stops being collectable.
         */}
        {cod ? (
          <Card variant="danger" className="mb-5">
            <CardBody className="flex items-start gap-3">
              <Wallet className="mt-0.5 size-5 shrink-0 text-error-ink" aria-hidden="true" />
              <div className="min-w-0">
                <p data-numeric className="font-semibold leading-snug text-error-ink">
                  Collect {formatMoney(delivery.codAmount)} in cash
                </p>
                <p className="mt-0.5 text-sm leading-snug text-ink-muted">
                  This is recorded against you the moment the code is accepted.
                </p>
              </div>
            </CardBody>
          </Card>
        ) : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void complete();
          }}
          className="grid gap-4"
        >
          <div className="grid gap-1.5">
            <label htmlFor="handover-code" className="text-sm font-medium text-ink">
              Their four-digit code
            </label>
            <input
              id="handover-code"
              /*
               * `inputMode` rather than `type="number"`: a number input
               * strips leading zeros, and a quarter of these codes start
               * with one. The pattern is what summons the digit keypad on
               * iOS; the filter below is what enforces it.
               */
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              /*
               * Deliberately NO `maxLength`.
               *
               * The browser clamps to it BEFORE React sees the value, so a
               * paste of "12ab34" arrives as "12ab" and the filter below turns
               * it into "12" — two digits silently lost out of four. The
               * handler is the single enforcement point instead: strip to
               * digits first, then take four.
               */
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, '').slice(0, 4));
                setError(null);
              }}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'handover-error' : undefined}
              placeholder="- - - -"
              data-numeric
              autoFocus
              className={[
                'h-20 w-full rounded-xl border bg-raised text-center text-4xl font-bold tracking-[0.5em]',
                'text-ink placeholder:font-normal placeholder:tracking-[0.25em] placeholder:text-ink-subtle',
                'border-line-strong transition-[border-color,box-shadow]',
                'duration-[var(--motion-duration-fast)] ease-standard',
                'focus:border-line-brand focus:outline-none focus:ring-2 focus:ring-ring/25',
                'aria-[invalid=true]:border-error-line aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-error-ink/20',
              ].join(' ')}
            />
            {error ? (
              <p id="handover-error" role="alert" className="text-sm font-medium text-error-ink">
                {error}
              </p>
            ) : null}
          </div>

          {/* The photograph. Optional, and said so — a rider on a doorstep in
              the rain should not think the delivery is blocked on it. */}
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            // Opens the camera directly on a phone rather than the gallery,
            // which is where this photograph is actually taken.
            capture="environment"
            className="sr-only-text"
            /*
             * Named and taken out of the tab order, because the BUTTON below
             * is the control — this input is the mechanism behind it. Left
             * unnamed it is an unlabelled form field (a critical axe failure);
             * left tabbable it is a second, invisible stop that announces
             * nothing a rider can act on.
             */
            aria-label="Photograph of the delivery"
            tabIndex={-1}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void attachPhoto(file);
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="lg"
            block
            loading={uploading}
            loadingLabel="Uploading"
            onClick={() => fileInput.current?.click()}
          >
            {proofKey ? <Check aria-hidden="true" /> : <Camera aria-hidden="true" />}
            {proofKey ? 'Photo attached — retake' : 'Add a photo (optional)'}
          </Button>

          <Button
            type="submit"
            size="xl"
            block
            disabled={!ready}
            loading={working}
            loadingLabel="Completing"
          >
            Complete delivery
          </Button>

          {uploading ? (
            <p className="flex items-center justify-center gap-2 text-sm text-ink-subtle">
              <LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              Sending the photo. You can still type the code.
            </p>
          ) : null}
        </form>
      </SheetContent>
    </Sheet>
  );
}
